import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from '../../utils/axios';
import { toast } from 'react-hot-toast';
import {
  ArrowRightIcon,
  PrinterIcon,
  XMarkIcon,
  UserIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  CreditCardIcon,
  TruckIcon,
  ShoppingBagIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  MapPinIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import EmptyState from '../../components/ui/EmptyState';
import AccessibleButton from '../../components/ui/AccessibleButton';
import Breadcrumb from '../../components/ui/Breadcrumb';

const B2BOrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  console.log('📄 B2BOrderDetailPage loaded');
  console.log('   - Order ID from params:', orderId);
  console.log('   - Current URL:', window.location.href);

  useEffect(() => {
    console.log('📄 B2BOrderDetailPage useEffect running');
    console.log('   - Order ID:', orderId);
    console.log('   - Current URL:', window.location.href);
    
    if (!orderId) {
      console.log('⚠️ No orderId provided');
      setError('מספר הזמנה לא תקין');
      setLoading(false);
      return;
    }
    
    const fetchOrder = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('token');
        console.log('   - Token exists:', !!token);
        console.log('   - Fetching order:', `/api/orders/${orderId}`);
        
        const response = await axios.get(`/api/orders/${orderId}`);
        console.log('   - Order fetched successfully:', response.data);
        
        const orderData = response.data.order || response.data;
        if (!orderData) {
          throw new Error('Order data not found in response');
        }
        
        setOrder(orderData);
        console.log('   - Order set in state:', orderData.id || orderData.order_id);
      } catch (err) {
        console.error('❌ Error fetching order:', err);
        console.error('   - Error response:', err.response?.data);
        console.error('   - Error status:', err.response?.status);
        const errorMessage = err.response?.data?.message || 
                           err.response?.data?.error || 
                           err.message || 
                           'Failed to load order';
        setError(errorMessage);
        toast.error(`שגיאה בטעינת פרטי ההזמנה: ${errorMessage}`);
        
        // Redirect to orders list if 404
        if (err.response?.status === 404) {
          setTimeout(() => {
            navigate('/orders', { replace: true });
          }, 2000);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, navigate]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-purple-100 text-purple-800',
      shipped: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'ממתין לאישור',
      confirmed: 'מאושר',
      processing: 'בעיבוד',
      shipped: 'נשלח',
      delivered: 'נמסר',
      cancelled: 'בוטל',
      refunded: 'הוחזר'
    };
    return texts[status] || status;
  };

  const getPaymentMethodLabel = (method) => {
    const methods = {
      'cash_on_delivery': 'מזומן בזמן האספקה',
      'credit_card': 'כרטיס אשראי',
      'bank_transfer': 'העברה בנקאית',
      'check': 'צ\'ק',
      'company_credit': 'אשראי חברה',
      'installments': 'תשלומים',
      'wire_transfer': 'העברה בינלאומית',
      'cash': 'מזומן'
    };
    return methods[method] || method;
  };

  if (loading) {
    return (
      <LoadingSpinner 
        fullScreen 
        size="xl" 
        color="primary" 
        text="טוען פרטי הזמנה..."
      />
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 py-12" dir="rtl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <EmptyState
            preset="error"
            title="שגיאה בטעינת ההזמנה"
            message={error || 'הזמנה לא נמצאה'}
            action={{
              label: 'חזור להזמנות',
              onClick: () => navigate('/orders')
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 pb-safe" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" role="main" aria-label="פרטי הזמנה">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: 'הזמנות', path: '/orders' },
            { label: `הזמנה #${order.order_number || order.id}`, path: `/orders/${orderId}` }
          ]}
          className="mb-6"
        />

        {/* Header */}
        <header className="mb-6 sm:mb-8" role="banner">
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-5 sm:p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                  פרטי הזמנה #{order.order_number || order.id}
                </h1>
                <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm sm:text-base text-gray-600">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-gray-400" />
                    <span>נוצרה ב-{formatDate(order.created_at)}</span>
                  </div>
                  {order.delivery_date && (
                    <div className="flex items-center gap-2">
                      <TruckIcon className="h-4 w-4 text-gray-400" />
                      <span>תאריך אספקה משוער: {formatDate(order.delivery_date)}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <span 
                  className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 ${getStatusColor(order.status)}`}
                  role="status"
                  aria-label={`סטטוס הזמנה: ${getStatusText(order.status)}`}
                >
                  {order.status === 'delivered' && <CheckCircleIcon className="h-4 w-4" />}
                  {order.status === 'cancelled' && <XMarkIcon className="h-4 w-4" />}
                  {order.status === 'pending' && <ClockIcon className="h-4 w-4" />}
                  {getStatusText(order.status)}
                </span>
                <AccessibleButton
                  onClick={() => window.print()}
                  variant="outline"
                  size="md"
                  icon={PrinterIcon}
                  iconPosition="left"
                  ariaLabel="הדפס פרטי הזמנה"
                >
                  הדפס
                </AccessibleButton>
              </div>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Items */}
            <section className="bg-white rounded-xl shadow-lg border border-gray-100 p-5 sm:p-6 transition-all hover:shadow-xl" aria-label="פריטי ההזמנה">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200">
                <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg p-2">
                  <ShoppingBagIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                  פריטי ההזמנה
                </h2>
                {order.items && order.items.length > 0 && (
                  <span className="mr-auto bg-blue-100 text-blue-700 text-xs font-bold px-2.5 py-1 rounded-full">
                    {order.items.length} פריטים
                  </span>
                )}
              </div>
              <div className="space-y-4" role="list">
                {order.items && order.items.length > 0 ? (
                  order.items.map((item, index) => {
                    const imageUrl = item.product?.images?.[0] 
                      ? (item.product.images[0].startsWith('http') ? item.product.images[0] : `/api${item.product.images[0]}`)
                      : item.product?.image || 'https://via.placeholder.com/80';
                    const itemTotal = (item.unit_price || item.price || 0) * (item.quantity || 1);
                    
                    return (
                      <div 
                        key={item.id || index} 
                        className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 hover:shadow-md transition-all duration-200 border border-gray-100 group touch-manipulation"
                        role="listitem"
                        aria-label={`${item.product?.name || item.product_name || 'מוצר'}, כמות: ${item.quantity}, מחיר: ${formatCurrency(itemTotal)}`}
                      >
                        <div className="flex-shrink-0">
                          <img
                            src={imageUrl}
                            alt={item.product?.name || item.product_name || 'תמונת מוצר'}
                            className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg shadow-sm border-2 border-white group-hover:scale-105 transition-transform duration-200"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1 line-clamp-2">
                            {item.product?.name || item.product_name || 'מוצר'}
                          </h3>
                          {item.product?.sku && (
                            <p className="text-xs sm:text-sm text-gray-500 mb-2 font-mono">
                              SKU: {item.product.sku}
                            </p>
                          )}
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                                כמות: {item.quantity}
                              </span>
                              <span className="text-xs sm:text-sm text-gray-600">
                                מחיר יחידה: {formatCurrency(item.unit_price || item.price || 0)}
                              </span>
                            </div>
                            <span className="text-base sm:text-lg font-bold text-gray-900">
                              {formatCurrency(itemTotal)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <EmptyState
                    preset="orders"
                    title="אין פריטים בהזמנה"
                    message="לא נמצאו פריטים בהזמנה זו"
                  />
                )}
              </div>
            </section>

            {/* Order Notes */}
            {order.notes && (
              <section className="bg-white rounded-xl shadow-lg border border-gray-100 p-5 sm:p-6 transition-all hover:shadow-xl" aria-label="הערות להזמנה">
                <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
                  <div className="bg-gradient-to-br from-yellow-500 to-orange-600 rounded-lg p-2">
                    <DocumentTextIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                    הערות להזמנה
                  </h2>
                </div>
                <p className="text-sm sm:text-base text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {order.notes}
                </p>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6 lg:sticky lg:top-8" aria-label="סיכום הזמנה">
            {/* Order Summary */}
            <section className="bg-white rounded-xl shadow-lg border border-gray-100 p-5 sm:p-6 transition-all hover:shadow-xl">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200">
                <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg p-2">
                  <ShoppingBagIcon className="h-5 w-5 text-white" />
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                  סיכום הזמנה
                </h2>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm sm:text-base">
                  <span className="text-gray-600 flex items-center gap-2">
                    <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
                    סכום ביניים
                  </span>
                  <span className="font-semibold text-gray-900">{formatCurrency(order.subtotal || order.subtotal_amount || 0)}</span>
                </div>
                {(order.shipping_cost > 0 || order.shipping_amount > 0) && (
                  <div className="flex justify-between items-center text-sm sm:text-base">
                    <span className="text-gray-600 flex items-center gap-2">
                      <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
                      משלוח
                    </span>
                    <span className="font-semibold">
                      {((order.shipping_cost || order.shipping_amount || 0) === 0) ? (
                        <span className="text-green-600 flex items-center gap-1">
                          <CheckCircleIcon className="h-4 w-4" />
                          חינם
                        </span>
                      ) : (
                        formatCurrency(order.shipping_cost || order.shipping_amount || 0)
                      )}
                    </span>
                  </div>
                )}
                {(order.tax_amount > 0 || order.tax > 0) && (
                  <div className="flex justify-between items-center text-sm sm:text-base">
                    <span className="text-gray-600 flex items-center gap-2">
                      <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
                      מע"מ
                    </span>
                    <span className="font-semibold text-gray-900">{formatCurrency(order.tax_amount || order.tax || 0)}</span>
                  </div>
                )}
                <div className="border-t-2 border-blue-200 pt-3 mt-3 bg-gradient-to-r from-blue-50 to-purple-50 -mx-3 px-3 py-2 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-base sm:text-lg font-bold text-gray-900">סה"כ לתשלום</span>
                    <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      {formatCurrency(order.total_amount || order.total || 0)}
                    </span>
                  </div>
                </div>
              </div>
            </section>

            {/* Payment Info */}
            <section className="bg-white rounded-xl shadow-lg border border-gray-100 p-5 sm:p-6 transition-all hover:shadow-xl" aria-label="פרטי תשלום">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200">
                <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg p-2">
                  <CreditCardIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                  פרטי תשלום
                </h2>
              </div>
              <div className="space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs sm:text-sm text-gray-600 mb-1 flex items-center gap-2">
                    <CreditCardIcon className="h-4 w-4" />
                    אמצעי תשלום
                  </p>
                  <p className="text-sm sm:text-base font-semibold text-gray-900">
                    {getPaymentMethodLabel(order.payment_method)}
                  </p>
                </div>
                {order.purchase_order_number && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-xs sm:text-sm text-gray-600 mb-1 flex items-center gap-2">
                      <DocumentTextIcon className="h-4 w-4" />
                      מספר הזמנת רכש
                    </p>
                    <p className="text-sm sm:text-base font-semibold text-gray-900 font-mono">
                      {order.purchase_order_number}
                    </p>
                  </div>
                )}
                {order.payment_status && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs sm:text-sm text-gray-600 mb-1">סטטוס תשלום</p>
                    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${
                      order.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                      order.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {order.payment_status === 'paid' && <CheckCircleIcon className="h-4 w-4" />}
                      {order.payment_status === 'pending' && <ClockIcon className="h-4 w-4" />}
                      {order.payment_status === 'paid' ? 'שולם' : 
                       order.payment_status === 'pending' ? 'ממתין לתשלום' : 
                       order.payment_status}
                    </span>
                  </div>
                )}
              </div>
            </section>

            {/* Shipping Address */}
            {order.shipping_address && (
              <section className="bg-white rounded-xl shadow-lg border border-gray-100 p-5 sm:p-6 transition-all hover:shadow-xl" aria-label="כתובת משלוח">
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200">
                  <div className="bg-gradient-to-br from-indigo-500 to-blue-600 rounded-lg p-2">
                    <TruckIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                    כתובת משלוח
                  </h2>
                </div>
                <div className="space-y-3 text-sm sm:text-base text-gray-700">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                      <UserIcon className="h-4 w-4 text-gray-400" />
                      {order.shipping_address.first_name} {order.shipping_address.last_name}
                    </p>
                    {order.shipping_address.company_name && (
                      <p className="flex items-center gap-2 text-gray-600 mt-1">
                        <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                        {order.shipping_address.company_name}
                      </p>
                    )}
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="flex items-start gap-2">
                      <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <span>
                        {order.shipping_address.address_line_1 || order.shipping_address.address}
                        {order.shipping_address.address_line_2 && (
                          <span>, {order.shipping_address.address_line_2}</span>
                        )}
                        <br />
                        {order.shipping_address.city}, {order.shipping_address.postal_code}
                      </span>
                    </p>
                  </div>
                  {order.shipping_address.phone && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="flex items-center gap-2">
                        <PhoneIcon className="h-4 w-4 text-gray-400" />
                        {order.shipping_address.phone}
                      </p>
                    </div>
                  )}
                  {order.shipping_address.email && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="flex items-center gap-2">
                        <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                        {order.shipping_address.email}
                      </p>
                    </div>
                  )}
                </div>
              </section>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default B2BOrderDetailPage;

